import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import maplibregl, {
  Map as MaplibreMap,
  Marker,
  Popup,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { MerchantNetworkRow } from '@/types/merchants';
import type { Geofence, UserPoiCollection } from '@/types/osm';
import type {
  HeatmapColorScheme,
  HeatmapFeatureCollection,
} from '@/types/heatmap';
import type { FraudPin, HeatmapMetric, MapAction } from '@/types/askAi';
import { OSM_STYLE_URL, transformRequest, transformStyle } from '@/lib/mapStyle';

const INITIAL_CENTER: [number, number] = [24.6, -29.0];
const INITIAL_ZOOM = 5;

// Mainland South Africa land bounding box [[W,S],[E,N]]. We fitBounds() to this
// on load so the country fills the (full-bleed) canvas with the least possible
// blue water, instead of the zoomed-out regional view a static center/zoom gives.
const SA_BOUNDS: maplibregl.LngLatBoundsLike = [
  [16.45, -34.85],
  [32.95, -22.13],
];

export const VEHICLE_COLOURS = [
  '#1F6FB8',
  '#E66A1F',
  '#3E9C4A',
  '#8A4FA0',
  '#C73838',
  '#0F8C7E',
  '#A8852C',
  '#D24A78',
];

export const PRIMARY = 'hsl(217 91% 60%)';

export type MapClickPayload = { lng: number; lat: number; rightClick: boolean };

export interface FullMapHandle {
  getMap(): MaplibreMap | null;
  flyTo(lng: number, lat: number, zoom?: number): void;
  applyAction(action: MapAction): void;
  addHeatmap(
    metric: HeatmapMetric,
    features: HeatmapFeatureCollection,
    maxWeight: number,
    colorScheme: HeatmapColorScheme,
  ): void;
  removeHeatmap(metric: HeatmapMetric): void;
  set3D(on: boolean): void;
  hasHeatmap(metric: HeatmapMetric): boolean;
  is3D(): boolean;
}

const HEAT_RAMPS: Record<HeatmapColorScheme, [number, string][]> = {
  // Ramp shifted aggressive-low so faint kernel densities at country zoom
  // still register as a visible colour rather than near-white pixels.
  red: [
    [0, 'rgba(0,0,0,0)'],
    [0.05, '#fca5a5'],
    [0.3, '#f87171'],
    [0.6, '#dc2626'],
    [1, '#7f1d1d'],
  ],
  orange: [
    [0, 'rgba(0,0,0,0)'],
    [0.05, '#fdba74'],
    [0.3, '#fb923c'],
    [0.6, '#ea580c'],
    [1, '#7c2d12'],
  ],
  amber: [
    [0, 'rgba(0,0,0,0)'],
    [0.05, '#fcd34d'],
    [0.3, '#f59e0b'],
    [0.6, '#d97706'],
    [1, '#78350f'],
  ],
  blue: [
    [0, 'rgba(0,0,0,0)'],
    [0.05, '#93c5fd'],
    [0.3, '#60a5fa'],
    [0.6, '#2563eb'],
    [1, '#1e3a8a'],
  ],
};

const BUILDING_LAYER_ID = 'fb-3d-buildings';

interface FullMapProps {
  onMapClick?: (p: MapClickPayload) => void;
  merchants: MerchantNetworkRow[];
  showMerchants: boolean;
  userPois: UserPoiCollection | null;
  showUserPois: boolean;
  showOsmPois: boolean;
  enabledOsmCategories: Set<string>;
  geofences: Geofence[];
  drawPreviewRing: number[][] | null;
}

const ZAR = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 });

const CATEGORY_COLOURS: Record<string, string> = {
  dealership: '#0F8C7E',
  repair: '#E66A1F',
  panel_beater: '#B33980',
  tyres: '#1a1a1a',
  breakdown: '#C73838',
  parts: '#8A4FA0',
  fuel: '#1F6FB8',
  merchant: '#A8852C',
  custom: '#2E5A8C',
};

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function merchantPopup(m: MerchantNetworkRow): string {
  const name = escHtml(m.trade_name ?? m.merchant_code);
  const code = escHtml(m.merchant_code);
  const brand = escHtml(m.brand ?? '—');
  return `
    <div style="font-family:Inter,system-ui,sans-serif;font-size:12px;min-width:200px">
      <div style="font-weight:600;font-size:13px">${name}</div>
      <div style="color:#64748b;margin-bottom:4px">${code} · ${brand}</div>
      <div>Spend 90d: <strong>${ZAR.format(m.spend_90d_rands)}</strong></div>
      <div>Txns 90d: ${m.txn_count_90d}</div>
      ${m.is_high_risk ? '<div style="color:#b91c1c;font-weight:600;margin-top:4px">High-risk</div>' : ''}
    </div>`;
}

// Severity → pin colour (matches the fraud-hub severity ramp).
const FRAUD_SEVERITY_COLOUR: Record<string, string> = {
  Critical: '#7f1d1d',
  High: '#dc2626',
  Medium: '#ea580c',
  Low: '#f59e0b',
  Watch: '#64748b',
  Normal: '#22c55e',
};

// Server-grounded fraud-pin popup: `RULE · SEVERITY — why_summary`. Every field
// is HTML-escaped — why_summary is LLM-authored text (XSS surface).
function fraudPinPopup(p: FraudPin): string {
  const rule = escHtml(p.rule_id ?? 'FRAUD');
  const sev = p.severity ? ` · ${escHtml(p.severity)}` : '';
  const risk = p.risk_score != null ? ` (risk ${p.risk_score})` : '';
  const why = p.why_summary
    ? `<div style="color:#334155;margin-top:3px">${escHtml(p.why_summary)}</div>`
    : '';
  const kind = p.transaction_kind ? escHtml(p.transaction_kind) : '';
  const when = p.alert_time
    ? `<div style="color:#94a3b8;margin-top:2px">${escHtml(
        new Date(p.alert_time).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }),
      )}</div>`
    : '';
  return `
    <div style="font-family:Inter,system-ui,sans-serif;font-size:12px;min-width:210px">
      <div style="font-weight:600;color:#b91c1c">⚠ ${rule}${sev}${risk}</div>
      ${kind ? `<div style="color:#64748b">${kind}</div>` : ''}
      ${why}${when}
    </div>`;
}

function poiPopup(p: Record<string, unknown>): string {
  const name = escHtml(String(p.name ?? 'Unnamed'));
  const cat = escHtml(String(p.category ?? '—'));
  const addr = p.address ? `<div style="color:#64748b">${escHtml(String(p.address))}</div>` : '';
  const phone = p.phone ? `<div>☎ ${escHtml(String(p.phone))}</div>` : '';
  return `
    <div style="font-family:Inter,system-ui,sans-serif;font-size:12px;min-width:180px">
      <div style="font-weight:600">${name}</div>
      <div style="color:#64748b;margin-bottom:4px">${cat}</div>
      ${addr}${phone}
    </div>`;
}

const POI_CATEGORY_CLASSES: Record<string, string[]> = {
  fuel: ['fuel'],
  food: ['restaurant', 'cafe', 'fast_food', 'bar', 'beer', 'ice_cream'],
  lodging: ['lodging'],
  shop: ['shop', 'grocery', 'clothing_store', 'alcohol_shop', 'convenience'],
  health: ['hospital', 'pharmacy', 'dentist'],
  transit: ['bus', 'railway', 'aerodrome', 'entrance', 'harbor'],
  parking: ['parking'],
  leisure: ['park', 'golf', 'stadium', 'swimming', 'playground', 'sports_centre'],
  civic: ['police', 'town_hall', 'post', 'fire_station', 'courthouse', 'prison'],
  religion: ['religious', 'place_of_worship'],
  tourism: ['attraction', 'museum', 'castle', 'art_gallery', 'monument', 'viewpoint'],
  entertainment: ['theatre', 'cinema', 'casino', 'nightclub', 'amusement_park'],
  info: ['atm', 'bank', 'information', 'telephone', 'toilets', 'recycling', 'shelter'],
  toll_booth: ['toll_booth'],
  lift_gate: ['lift_gate'],
  speed_camera: ['speed_camera'],
  traffic_signals: ['traffic_signals'],
  stops_giveway: ['stop', 'give_way', 'stop_sign'],
  street_lamp: ['street_lamp'],
};

const FullMap = forwardRef<FullMapHandle, FullMapProps>(function FullMap(
  {
    onMapClick,
    merchants,
    showMerchants,
    userPois,
    showUserPois,
    showOsmPois,
    enabledOsmCategories,
    geofences,
    drawPreviewRing,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const merchantMarkersRef = useRef<Marker[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [styleError, setStyleError] = useState<string | null>(null);
  const poiSymbolLayerIdsRef = useRef<string[]>([]);
  const heatmapLayersRef = useRef<Map<HeatmapMetric, { layerId: string; sourceId: string }>>(
    new Map(),
  );
  const aiPinMarkersRef = useRef<Marker[]>([]);
  const is3DRef = useRef<boolean>(false);

  function ensureHeatmap(
    metric: HeatmapMetric,
    features: HeatmapFeatureCollection,
    maxWeight: number,
    colorScheme: HeatmapColorScheme,
  ) {
    const map = mapRef.current;
    if (!map) return;
    const layerId = `fb-heat-${metric}`;
    const sourceId = `fb-heat-src-${metric}`;
    if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);

    map.addSource(sourceId, { type: 'geojson', data: features as never });

    void maxWeight;
    const ramp = HEAT_RAMPS[colorScheme];
    const colorExpr: unknown[] = ['interpolate', ['linear'], ['heatmap-density']];
    for (const [stop, colour] of ramp) colorExpr.push(stop, colour);

    map.addLayer({
      id: layerId,
      type: 'heatmap',
      source: sourceId,
      maxzoom: 16,
      paint: {
        // 'weight' from the API is ALREADY normalised 0-1 (raw / max_weight).
        // Don't re-interpolate against max — that double-normalises and the
        // computed weight collapses to ~0, making the layer invisible.
        'heatmap-weight': ['get', 'weight'] as never,
        'heatmap-intensity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          3,
          5,
          4,
          10,
          2.5,
          16,
          1.5,
        ] as never,
        'heatmap-color': colorExpr as never,
        'heatmap-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          12,
          5,
          28,
          10,
          40,
          16,
          55,
        ] as never,
        'heatmap-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          7,
          0.85,
          16,
          0.35,
        ] as never,
      },
    });
    heatmapLayersRef.current.set(metric, { layerId, sourceId });
  }

  function dropHeatmap(metric: HeatmapMetric) {
    const map = mapRef.current;
    if (!map) return;
    const entry = heatmapLayersRef.current.get(metric);
    if (!entry) return;
    if (map.getLayer(entry.layerId)) map.removeLayer(entry.layerId);
    if (map.getSource(entry.sourceId)) map.removeSource(entry.sourceId);
    heatmapLayersRef.current.delete(metric);
  }

  function set3DInternal(on: boolean) {
    const map = mapRef.current;
    if (!map) return;
    is3DRef.current = on;
    if (on) {
      map.easeTo({ pitch: 60, bearing: -10, duration: 600 });
      if (!map.getLayer(BUILDING_LAYER_ID)) {
        try {
          map.addLayer({
            id: BUILDING_LAYER_ID,
            type: 'fill-extrusion',
            source: 'openmaptiles',
            'source-layer': 'building',
            minzoom: 14,
            paint: {
              'fill-extrusion-color': '#cbd5e1',
              'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 8] as never,
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0] as never,
              'fill-extrusion-opacity': 0.7,
            },
          });
        } catch {
          /* style may not have openmaptiles building layer */
        }
      }
    } else {
      map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
      if (map.getLayer(BUILDING_LAYER_ID)) map.removeLayer(BUILDING_LAYER_ID);
    }
  }

  function dropPin(lng: number, lat: number, label?: string) {
    const map = mapRef.current;
    if (!map) return;
    const el = document.createElement('div');
    el.style.cssText =
      'width:14px;height:14px;border-radius:50%;background:#dc2626;border:3px solid white;' +
      'box-shadow:0 2px 6px rgba(0,0,0,0.45);cursor:pointer';
    const marker = new Marker({ element: el }).setLngLat([lng, lat]);
    if (label) {
      marker.setPopup(
        new Popup({ offset: 14 }).setHTML(
          `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px;font-weight:600">${escHtml(label)}</div>`,
        ),
      );
    }
    marker.addTo(map);
    aiPinMarkersRef.current.push(marker);
  }

  // Drop SERVER-GROUNDED fraud pins (from a `plot_fraud_points` action). Pins
  // are cleared-on-replot: each call removes the previous AI pin set first so a
  // new question never stacks stale pins on the map. Coordinates come only from
  // the server (analytics.fraud_point) — never from LLM output.
  function plotFraudPoints(points: FraudPin[]) {
    const map = mapRef.current;
    if (!map) return;
    aiPinMarkersRef.current.forEach((m) => m.remove());
    aiPinMarkersRef.current = [];
    const lngs: number[] = [];
    const lats: number[] = [];
    for (const p of points) {
      if (typeof p.lon !== 'number' || typeof p.lat !== 'number') continue;
      if (p.lon < -180 || p.lon > 180 || p.lat < -90 || p.lat > 90) continue;
      const colour = FRAUD_SEVERITY_COLOUR[p.severity ?? ''] ?? '#dc2626';
      const el = document.createElement('div');
      el.style.cssText =
        `width:14px;height:14px;border-radius:50%;background:${colour};border:3px solid white;` +
        'box-shadow:0 2px 6px rgba(0,0,0,0.45);cursor:pointer';
      const marker = new Marker({ element: el })
        .setLngLat([p.lon, p.lat])
        .setPopup(new Popup({ offset: 14 }).setHTML(fraudPinPopup(p)))
        .addTo(map);
      aiPinMarkersRef.current.push(marker);
      lngs.push(p.lon);
      lats.push(p.lat);
    }
    if (lngs.length > 1) {
      map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 60, duration: 800 },
      );
    } else if (lngs.length === 1) {
      map.flyTo({ center: [lngs[0], lats[0]], zoom: 13, duration: 800, essential: true });
    }
  }

  function applyActionInternal(action: MapAction) {
    const map = mapRef.current;
    if (!map) return;
    switch (action.action) {
      case 'plot_fraud_points': {
        plotFraudPoints(action.points ?? []);
        return;
      }
      case 'replay_vehicle': {
        // Driven by Maps.tsx panel state (VehicleReplayPanel owns the replay
        // logic; FullMap can't reach it). No-op here.
        return;
      }
      case 'zoom_to': {
        if (action.lng == null || action.lat == null) return;
        map.flyTo({
          center: [action.lng, action.lat],
          zoom: action.zoom ?? 12,
          duration: 800,
          essential: true,
        });
        return;
      }
      case 'fit_bounds': {
        if (!action.bounds) return;
        map.fitBounds(action.bounds, {
          padding: action.padding ?? 60,
          duration: 800,
        });
        return;
      }
      case 'drop_pin': {
        if (action.lng == null || action.lat == null) return;
        dropPin(action.lng, action.lat, action.label);
        return;
      }
      case 'set_3d': {
        const on = (action.pitch ?? 60) > 0;
        set3DInternal(on);
        if (on && action.bearing != null) {
          map.setBearing(action.bearing);
        }
        return;
      }
      case 'show_pois': {
        const ids = poiSymbolLayerIdsRef.current;
        if (!ids.length || !action.category) return;
        for (const id of ids) {
          try {
            map.setLayoutProperty(id, 'visibility', 'visible');
            map.setFilter(id, ['==', ['get', 'class'], action.category] as never);
          } catch {
            /* ignore */
          }
        }
        return;
      }
      case 'show_heatmap': {
        // Heatmap loading is orchestrated at the page level (Maps.tsx) so it
        // can use TanStack Query for caching/error UI; FullMap only renders
        // results via addHeatmap().
        return;
      }
      default:
        return;
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      getMap: () => mapRef.current,
      flyTo: (lng, lat, zoom = 14) => {
        mapRef.current?.flyTo({ center: [lng, lat], zoom, essential: true });
      },
      applyAction: (a) => applyActionInternal(a),
      addHeatmap: (metric, features, max, scheme) =>
        ensureHeatmap(metric, features, max, scheme),
      removeHeatmap: (metric) => dropHeatmap(metric),
      set3D: (on) => set3DInternal(on),
      hasHeatmap: (metric) => heatmapLayersRef.current.has(metric),
      is3D: () => is3DRef.current,
    }),
    [],
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: { version: 8, sources: {}, layers: [] },
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      transformRequest,
      attributionControl: { compact: true },
    });
    map.setStyle(OSM_STYLE_URL, { transformStyle });
    mapRef.current = map;

    map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
    map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
      }),
      'top-right',
    );

    const handleResize = () => map.resize();
    window.addEventListener('resize', handleResize);
    // The map's flex parent can change size after mount (sidebar collapse,
    // panel toggle, container hidden during route transition). MapLibre only
    // re-reads container dims on explicit resize() — without an observer the
    // canvas stays at whatever dims it had at construction time, which can
    // be 0×0 if layout hadn't settled yet.
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);

    map.on('load', () => {
      setLoaded(true);
      setStyleError(null);
      const layers = map.getStyle()?.layers ?? [];
      poiSymbolLayerIdsRef.current = layers
        .filter((l) => l.type === 'symbol' && /poi/i.test(l.id))
        .map((l) => l.id);
      // resize() so the canvas picks up the settled flex dimensions, THEN frame
      // South Africa — order matters: fitBounds uses the current canvas size.
      map.resize();
      map.fitBounds(SA_BOUNDS, { padding: 16, duration: 0 });
    });

    map.on('error', (e) => {
      const msg = (e as { error?: Error }).error?.message ?? 'map error';
      if (/style/i.test(msg) || /Failed to fetch/i.test(msg)) {
        setStyleError(msg);
      }
    });

    map.on('click', (e) => {
      onMapClick?.({ lng: e.lngLat.lng, lat: e.lngLat.lat, rightClick: false });
    });
    map.on('contextmenu', (e) => {
      e.preventDefault();
      onMapClick?.({ lng: e.lngLat.lng, lat: e.lngLat.lat, rightClick: true });
    });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
      merchantMarkersRef.current.forEach((m) => m.remove());
      merchantMarkersRef.current = [];
      aiPinMarkersRef.current.forEach((m) => m.remove());
      aiPinMarkersRef.current = [];
      heatmapLayersRef.current.clear();
      map.remove();
      mapRef.current = null;
      setLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const ids = poiSymbolLayerIdsRef.current;
    if (!ids.length) return;
    const allowedClasses = new Set<string>();
    enabledOsmCategories.forEach((c) => {
      (POI_CATEGORY_CLASSES[c] ?? []).forEach((cls) => allowedClasses.add(cls));
    });
    for (const id of ids) {
      try {
        if (!showOsmPois) {
          map.setLayoutProperty(id, 'visibility', 'none');
        } else if (allowedClasses.size === 0) {
          map.setLayoutProperty(id, 'visibility', 'none');
        } else {
          map.setLayoutProperty(id, 'visibility', 'visible');
          const filter: unknown[] = ['in', ['get', 'class'], ['literal', Array.from(allowedClasses)]];
          try {
            map.setFilter(id, filter as never);
          } catch {
            /* some layers may not accept the filter shape */
          }
        }
      } catch {
        /* ignore per-layer errors */
      }
    }
  }, [loaded, showOsmPois, enabledOsmCategories]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    merchantMarkersRef.current.forEach((m) => m.remove());
    merchantMarkersRef.current = [];
    if (!showMerchants) return;
    const geocoded = merchants.filter((m) => m.latitude != null && m.longitude != null);
    for (const m of geocoded) {
      const el = document.createElement('div');
      el.style.width = '11px';
      el.style.height = '11px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = m.is_high_risk
        ? 'hsl(0 84% 60%)'
        : m.spend_90d_rands > 5_000_000
          ? 'hsl(48 96% 53%)'
          : 'hsl(142 71% 45%)';
      el.style.border = '2px solid white';
      el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.4)';
      el.style.cursor = 'pointer';
      const popup = new Popup({ offset: 12 }).setHTML(merchantPopup(m));
      const marker = new Marker({ element: el })
        .setLngLat([m.longitude!, m.latitude!])
        .setPopup(popup)
        .addTo(map);
      merchantMarkersRef.current.push(marker);
    }
  }, [merchants, showMerchants, loaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const SOURCE = 'fb-user-pois';
    const LAYER = 'fb-user-pois-circles';
    const CLUSTER_LAYER = 'fb-user-pois-clusters';
    const COUNT_LAYER = 'fb-user-pois-count';

    const removeAll = () => {
      for (const id of [COUNT_LAYER, CLUSTER_LAYER, LAYER]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SOURCE)) map.removeSource(SOURCE);
    };

    removeAll();
    if (!showUserPois || !userPois) return;

    map.addSource(SOURCE, {
      type: 'geojson',
      data: userPois,
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 11,
    });

    const matchExpr: unknown[] = ['match', ['get', 'category']];
    for (const [k, v] of Object.entries(CATEGORY_COLOURS)) {
      matchExpr.push(k, v);
    }
    matchExpr.push(CATEGORY_COLOURS.custom);

    map.addLayer({
      id: CLUSTER_LAYER,
      type: 'circle',
      source: SOURCE,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#1F3A5F',
        'circle-radius': ['step', ['get', 'point_count'], 14, 25, 18, 100, 24],
        'circle-opacity': 0.85,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'white',
      },
    });
    map.addLayer({
      id: COUNT_LAYER,
      type: 'symbol',
      source: SOURCE,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 11,
      },
      paint: { 'text-color': '#fff' },
    });
    map.addLayer({
      id: LAYER,
      type: 'circle',
      source: SOURCE,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 6,
        'circle-color': matchExpr as never,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'white',
      },
    });

    const onClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const f = e.features?.[0];
      if (!f) return;
      new Popup({ offset: 10 })
        .setLngLat(e.lngLat)
        .setHTML(poiPopup((f.properties ?? {}) as Record<string, unknown>))
        .addTo(map);
    };
    map.on('click', LAYER, onClick);
    return () => {
      map.off('click', LAYER, onClick);
      removeAll();
    };
  }, [userPois, showUserPois, loaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const removed: string[] = [];
    const layers = map.getStyle()?.layers ?? [];
    for (const l of layers) {
      if (l.id.startsWith('fb-fence-')) {
        if (map.getLayer(l.id)) map.removeLayer(l.id);
        removed.push(l.id);
      }
    }
    const sources = Object.keys(map.getStyle()?.sources ?? {});
    for (const sid of sources) {
      if (sid.startsWith('fb-fence-') && map.getSource(sid)) map.removeSource(sid);
    }
    for (const f of geofences) {
      const sid = `fb-fence-src-${f.id}`;
      const fillId = `fb-fence-fill-${f.id}`;
      const lineId = `fb-fence-line-${f.id}`;
      map.addSource(sid, {
        type: 'geojson',
        data: { type: 'Feature', properties: { id: f.id, name: f.name }, geometry: f.geometry as never },
      });
      map.addLayer({
        id: fillId,
        type: 'fill',
        source: sid,
        paint: { 'fill-color': '#2E5A8C', 'fill-opacity': 0.18 },
      });
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sid,
        paint: { 'line-color': '#2E5A8C', 'line-width': 2, 'line-opacity': 0.85 },
      });
    }
  }, [geofences, loaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const SRC = 'fb-fence-draw';
    const FILL = 'fb-fence-draw-fill';
    const LINE = 'fb-fence-draw-line';
    if (map.getLayer(FILL)) map.removeLayer(FILL);
    if (map.getLayer(LINE)) map.removeLayer(LINE);
    if (map.getSource(SRC)) map.removeSource(SRC);
    if (!drawPreviewRing || drawPreviewRing.length < 2) return;
    if (drawPreviewRing.length === 2) {
      map.addSource(SRC, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: drawPreviewRing },
        } as never,
      });
      map.addLayer({
        id: LINE,
        type: 'line',
        source: SRC,
        paint: { 'line-color': '#2E5A8C', 'line-width': 2, 'line-dasharray': [2, 2] },
      });
    } else {
      const ring = [...drawPreviewRing, drawPreviewRing[0]];
      map.addSource(SRC, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [ring] },
        } as never,
      });
      map.addLayer({
        id: FILL,
        type: 'fill',
        source: SRC,
        paint: { 'fill-color': '#2E5A8C', 'fill-opacity': 0.2 },
      });
      map.addLayer({
        id: LINE,
        type: 'line',
        source: SRC,
        paint: { 'line-color': '#2E5A8C', 'line-width': 2, 'line-dasharray': [2, 2] },
      });
    }
  }, [drawPreviewRing, loaded]);

  return (
    <div className="relative h-full w-full">
      {/* h-full + w-full instead of absolute inset-0: maplibre-gl.css sets
        * .maplibregl-map { position: relative } which overrides Tailwind's
        * `absolute` (same specificity, later stylesheet wins). On a relative
        * element `inset-0` defines no dimensions and the container collapses
        * to 0 height. */}
      <div ref={containerRef} className="h-full w-full" />
      {styleError && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 shadow">
          Map style failed to load — falling back to blank canvas. ({styleError})
        </div>
      )}
    </div>
  );
});

export default FullMap;
